import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import classes from './RangeDatePickerFilter.module.css'
import Button from '../../../../common/Button'
import OutsideClickComponent from '../../../../fragments/OutsideClickComponent'
import { formatDate, formatDateLocalRange } from '../../../../../lib/script'
import CustomDatePickerRange from '../../../../common/interactive/CustomDatePickerRange'
import { fitFixedPopoverToViewport } from '../../../../../lib/fitFixedPopoverToViewport'

export default function RangeDatePickerFilter({label, action, identifier, fullWidth, preSelectedValue, minMax}) {
    const [dateState, setDateState] = useState(false)
    const inputRef = useRef(null)
    const modalRef = useRef(null)
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 })

    const [dateObj, setDateObj] = useState({
        isRange: false,
        start: null,
        end: null
    })

    const updateModalPosition = useCallback(() => {
        if (!inputRef.current) {
            return
        }
        const next = fitFixedPopoverToViewport({
            triggerRect: inputRef.current.getBoundingClientRect(),
            popoverWidth: modalRef.current?.offsetWidth,
            popoverHeight: modalRef.current?.offsetHeight,
        })
        setModalPosition(next)
    }, [])

    function openDateHandler() {
        updateModalPosition()
        setDateState(true)
    }

    function closeDateHandler() {
        setDateState(false)
    }

    function setDateHandler(range) {
        let obj = {
            isRange: false,
            start: null,
            end: null
        }

        if (range) {
            if (range.startDate && range.endDate) {

                if (range.startDate == range.endDate) {
                    obj.isRange = false
                    obj.start = formatDateLocalRange(range.startDate)
                    obj.end = null

                    setDateObj(obj)
                } else {
                    obj.isRange = true
                    obj.start = formatDateLocalRange(range.startDate)
                    obj.end = formatDateLocalRange(range.endDate)

                    setDateObj(obj)
                }

            }


            if (action) {
                action({
                    data: {
                        value: range.startDate != range.endDate ? `${formatDate(range.startDate)}|${formatDate(range.endDate)}` : formatDate(range.startDate)
                    },

                    identifier: identifier,
                    option: {
                        label: range.startDate != range.endDate ? `${formatDate(range.startDate)}|${formatDate(range.endDate)}` : formatDate(range.startDate),
                        value: range.startDate != range.endDate ? `${formatDate(range.startDate)}|${formatDate(range.endDate)}` : formatDate(range.startDate)
                    }
                })
            }
        }

    }

    function removeDateHandler(event) {
        event?.stopPropagation?.()

        if (action) {
            action({
                data: {
                    value: null
                },             
                identifier: identifier,
                option: {
                    label:null,
                    value: null
                }
            })
        }

        setDateObj({
            isRange: false,
            start: null,
            end: null
        })
    }

    function dateValueText() {
        if (!dateObj?.start) {
            return null
        }
        if (dateObj.isRange) {
            return `From: ${dateObj.start} to ${dateObj.end}`
        }
        return dateObj.start
    }


    useEffect(() => {
        if (preSelectedValue) {
            if (preSelectedValue.includes('|')) {
                let dateArray = preSelectedValue.split('|')
                setDateObj({
                    isRange: true,
                    start: formatDateLocalRange(dateArray[0]),
                    end: formatDateLocalRange(dateArray[1])
                })
            } else {
                setDateObj({
                    isRange: false,
                    start: formatDateLocalRange(preSelectedValue),
                    end: null
                })
            }
        } else {
            setDateObj({
                isRange: false,
                start: null,
                end: null
            })
        }
    }, [preSelectedValue])

    useLayoutEffect(() => {
        if (!dateState) {
            return
        }
        updateModalPosition()
        // Re-measure after paint once calendar height is known
        const raf = window.requestAnimationFrame(() => updateModalPosition())
        const onReposition = () => updateModalPosition()
        window.addEventListener('resize', onReposition)
        window.addEventListener('scroll', onReposition, true)
        return () => {
            window.cancelAnimationFrame(raf)
            window.removeEventListener('resize', onReposition)
            window.removeEventListener('scroll', onReposition, true)
        }
    }, [dateState, updateModalPosition])

    const valueText = dateValueText()
    const isFocused = Boolean(dateState || valueText)
    const outsideClickExtraInsideRefs = useMemo(() => [modalRef], [])

    // Must use overlay-root so MegaFilter's ignorePortalRootClicks keeps the panel open
    const portalRoot = typeof document !== 'undefined'
        ? (document.getElementById('overlay-root') || document.getElementById('datepicker-portal') || document.body)
        : null

    const calendarModal = dateState ? (
        <div
            ref={modalRef}
            className={classes.modal_portal}
            style={{ top: modalPosition.top, left: modalPosition.left }}
        >
            <CustomDatePickerRange setDate={setDateHandler} preSelectedValue={preSelectedValue} minMax={minMax}/>
        </div>
    ) : null

    return <div className={`${classes.container} ${fullWidth ? classes.full_width : ''} ${isFocused ? classes.is_focused : ''} ${!label ? classes.no_label : ''}`} >
        <OutsideClickComponent task={closeDateHandler} extraInsideRefs={outsideClickExtraInsideRefs}>
            <div
                ref={inputRef}
                className={`${classes.input_container} ${preSelectedValue ? classes.has_value : ''}`}
                onClick={openDateHandler}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        openDateHandler()
                    }
                }}
            >
                {label ? <span className={classes.label}>{label}</span> : null}
                <div
                    className={`${classes.value} ${!valueText ? classes.placeholder : ''}`}
                    title={valueText || undefined}
                >
                    {valueText || (label ? '' : 'Select date')}
                </div>
                {preSelectedValue ? (
                    <div className={classes.clear_btn}>
                        <Button click={removeDateHandler} btnStyle={'btn_simple'} title={'remove'}>
                            <i className="ti ti-x"></i>
                        </Button>
                    </div>
                ) : null}
            </div>

            {/* Portal inside OutsideClick wrapper (same pattern as CustomSelect) */}
            {calendarModal && portalRoot
                ? ReactDOM.createPortal(calendarModal, portalRoot)
                : calendarModal}
        </OutsideClickComponent>
    </div>
}
